import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Link,
  Chip,
  InputAdornment,
} from '@mui/material';
import {
  Close,
  Info,
  Visibility,
  VisibilityOff,
  HelpOutline,
} from '@mui/icons-material';
import { useTheme } from '../../context/ThemeContext';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import customToolsService from '../../services/customToolsService';
import styles from './CustomToolConfigDialog.module.scss';

/**
 * Dialog for configuring custom tools
 * Handles configuration templates and credential management
 */
const CustomToolConfigDialog = ({
  open,
  onClose,
  tool,
  onRefresh,
  editConfig = null,
}) => {
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [configTemplate, setConfigTemplate] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState('gmail');
  const [configData, setConfigData] = useState({});
  const [configName, setConfigName] = useState('');
  const [configDescription, setConfigDescription] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const muiTheme = createTheme({
    palette: {
      mode: isDarkMode ? 'dark' : 'light',
      primary: {
        main: '#e36250',
      },
      secondary: {
        main: '#8b5cf6',
      },
    },
  });

  const fetchConfigTemplate = useCallback(async (toolName, provider) => {
    setLoading(true);
    try {
      const template = await customToolsService.getConfigTemplate(toolName, provider);
      setConfigTemplate(template);

      // If not editing, pre-fill with template defaults
      if (!editConfig) {
        setConfigData(template.config || {});
      }
    } catch (err) {
      console.error('Error fetching config template:', err);
      setError('Failed to load configuration template');
    } finally {
      setLoading(false);
    }
  }, [editConfig]);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open && tool) {
      setError(null);
      setSuccess(false);
      setLoading(false);
      setConfigData({});
      setShowPassword(false);

      // If editing, load existing config
      if (editConfig) {
        setSelectedProvider(editConfig.provider || 'custom');
        setConfigData(editConfig.config || {});
        setConfigName(editConfig.name || '');
        setConfigDescription(editConfig.description || '');
      } else {
        setSelectedProvider('gmail');
        setConfigName('');
        setConfigDescription('');
      }

      // Fetch config template
      fetchConfigTemplate(tool.id || tool.name, editConfig ? (editConfig.provider || 'custom') : 'gmail');
    }
  }, [open, tool, editConfig, fetchConfigTemplate]);

  const handleProviderChange = (event) => {
    const newProvider = event.target.value;
    setSelectedProvider(newProvider);
    fetchConfigTemplate(tool.id || tool.name, newProvider);
  };

  const handleConfigChange = (field, value) => {
    setConfigData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);

    // Validate required fields
    if (!configName.trim()) {
      setError('Please provide a name for this configuration');
      setLoading(false);
      return;
    }

    try {
      if (editConfig) {
        // Update existing configuration
        await customToolsService.updateToolConfig(editConfig.id, {
          name: configName,
          description: configDescription,
          config: configData,
          provider: selectedProvider
        });
      } else {
        // Save new configuration
        await customToolsService.saveToolConfig({
          tool_name: tool.id || tool.name,
          provider: selectedProvider,
          name: configName,
          description: configDescription,
          config: configData
        });
      }

      setSuccess(true);
      setTimeout(() => {
        if (onRefresh) {
          onRefresh();
        }
        onClose();
      }, 1500);
    } catch (err) {
      console.error('Error saving tool configuration:', err);
      setError(err.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setConfigData({});
    setError(null);
    setSuccess(false);
    onClose();
  };

  const getProviderHelpText = (provider) => {
    const helpTexts = {
      gmail: 'Note: For Gmail, you need to create an App Password instead of using your regular password. Go to your Google Account settings, enable 2-Step Verification, then generate an App Password for "Mail". Use this App Password in the password field below.',
      outlook: 'Note: For Outlook, you may need to create an App Password if you have 2-Step Verification enabled. Go to your Microsoft Account security settings and generate an App Password for "Mail". Use this App Password in the password field below.',
      office365: 'Note: For Office 365, you may need to create an App Password if you have Multi-Factor Authentication (MFA) enabled. Go to your Microsoft 365 security settings and generate an App Password. Use this App Password in the password field below.',
      custom: 'Enter your custom SMTP/IMAP server configuration details below. Make sure to use the correct server addresses, ports, and authentication credentials for your email provider.'
    };
    return helpTexts[provider] || helpTexts.custom;
  };

  const getProviderSetupUrl = (provider) => {
    const setupUrls = {
      gmail: 'https://support.google.com/accounts/answer/185833',
      outlook: 'https://support.microsoft.com/en-us/account-billing/using-app-passwords-with-apps-that-don-t-support-two-step-verification-5896ed9b-4263-e681-128a-a6f2979a7944',
      office365: 'https://support.microsoft.com/en-us/account-billing/using-app-passwords-with-apps-that-don-t-support-two-step-verification-5896ed9b-4263-e681-128a-a6f2979a7944',
      custom: null
    };
    return setupUrls[provider] || null;
  };

  const renderConfigField = (fieldName, fieldValue) => {
    const isPassword = fieldName.toLowerCase().includes('password') ||
                      fieldName.toLowerCase().includes('secret') ||
                      fieldName.toLowerCase().includes('token');
    const isNumber = typeof fieldValue === 'number';

    return (
      <TextField
        key={fieldName}
        fullWidth
        size="small"
        label={fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
        value={configData[fieldName] || ''}
        onChange={(e) => handleConfigChange(fieldName, isNumber ? parseInt(e.target.value) || '' : e.target.value)}
        type={isPassword && !showPassword ? 'password' : isNumber ? 'number' : 'text'}
        required={fieldName === 'email' || fieldName === 'password'}
        InputProps={isPassword ? {
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowPassword(!showPassword)}
                edge="end"
                size="small"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        } : undefined}
        sx={{ mb: 2 }}
      />
    );
  };

  if (!tool) return null;

  return (
    <ThemeProvider theme={muiTheme}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth={false}
        fullWidth
        scroll="paper"
        className={styles.dialog}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'rgba(24, 24, 27, 0.4)',
              backdropFilter: 'blur(2px)',
            },
          },
        }}
        PaperProps={{
          className: styles.dialogPaper,
        }}
      >
        <DialogTitle className={styles.dialogTitle} component="div">
          <Box className={styles.titleRow} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 1,
                bgcolor: 'action.hover',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.5rem',
                flexShrink: 0,
              }}
            >
              {tool.icon || '🔧'}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography component="h3" variant="h6" className={styles.title} sx={{ fontWeight: 600 }}>
                {editConfig ? 'Edit' : 'Configure'} {tool.name}
              </Typography>
              {tool.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  {tool.description}
                </Typography>
              )}
            </Box>
          </Box>
          <IconButton
            onClick={handleClose}
            className={styles.closeButton}
            aria-label="Close"
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent className={styles.dialogContent}>
          {loading && !configTemplate ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ mt: 0 }}>
              {/* Success Alert */}
              {success && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Configuration saved successfully!
                </Alert>
              )}

              {/* Error Alert */}
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {/* Configuration Name */}
              <TextField
                fullWidth
                size="small"
                label="Configuration Name *"
                value={configName}
                onChange={(e) => setConfigName(e.target.value)}
                placeholder="e.g., My Gmail Account, Work Email"
                sx={{ mb: 2 }}
                required
              />

              {/* Configuration Description */}
              <TextField
                fullWidth
                size="small"
                label="Description (Optional)"
                value={configDescription}
                onChange={(e) => setConfigDescription(e.target.value)}
                placeholder="e.g., Personal Gmail for newsletters"
                multiline
                rows={2}
                sx={{ mb: 2 }}
              />

              {/* Provider Selection - only show when not editing */}
              {!editConfig && configTemplate && (
                <FormControl fullWidth size="small" sx={{ mb: 3 }}>
                  <InputLabel>Provider</InputLabel>
                  <Select
                    value={selectedProvider}
                    onChange={handleProviderChange}
                    label="Provider"
                  >
                    <MenuItem value="gmail">Gmail</MenuItem>
                    <MenuItem value="outlook">Outlook</MenuItem>
                    <MenuItem value="office365">Office 365</MenuItem>
                    <MenuItem value="custom">Custom</MenuItem>
                  </Select>
                </FormControl>
              )}

              {/* Help Text - Show provider-specific messages */}
              {configTemplate && (
                <Alert severity="info" icon={<Info />} sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    {configTemplate.help_text || getProviderHelpText(selectedProvider)}
                  </Typography>
                  {(configTemplate.setup_url || getProviderSetupUrl(selectedProvider)) && (
                    <Link
                      href={configTemplate.setup_url || getProviderSetupUrl(selectedProvider)}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}
                    >
                      <HelpOutline fontSize="small" />
                      Setup Guide
                    </Link>
                  )}
                </Alert>
              )}

              {/* Configuration Fields */}
              {configTemplate?.config && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                    Configuration Details
                  </Typography>
                  {Object.entries(configTemplate.config).map(([fieldName, fieldValue]) =>
                    renderConfigField(fieldName, fieldValue)
                  )}
                </Box>
              )}

              {/* Tool Category */}
              {tool.category && (
                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Category:
                  </Typography>
                  <Chip
                    label={tool.category}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions className={styles.dialogActions}>
          <Button
            onClick={handleClose}
            disabled={loading}
            variant="text"
            className={styles.cancelButton}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={loading || !configTemplate}
            className={styles.saveButton}
          >
            {loading ? (
              <CircularProgress
                size={20}
                sx={{
                  color: 'white',
                  '& .MuiCircularProgress-circle': {
                    strokeLinecap: 'round',
                  },
                }}
              />
            ) : (
              editConfig ? 'Update' : 'Save'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default CustomToolConfigDialog;
